import { useCallback, useMemo, useState } from "react";
import type { ISortConfig, ITableColumn, ITableSorting } from "../types";
import { defaultSort } from "../utils";

type Props<Item> = {
  items: Item[];
  sorting?: ITableSorting;
  columns?: ITableColumn[];
};

type UseSortControllerReturn<Item> = {
  sortedItems: Item[];
  sortConfig: ISortConfig | undefined;
  onSort: (key: string) => void;
};

export function useSortController<Item>({
  items,
  sorting,
  columns,
}: Props<Item>): UseSortControllerReturn<Item> {
  const [internalKey, setInternalKey] = useState<string | null>(null);
  const [internalDirection, setInternalDirection] = useState<
    "asc" | "desc" | null
  >(null);

  const onInternalSort = useCallback(
    (key: string) => {
      if (internalKey !== key) {
        setInternalKey(key);
        setInternalDirection("asc");
      } else if (internalDirection === "asc") {
        setInternalDirection("desc");
      } else {
        setInternalKey(null);
        setInternalDirection(null);
      }
    },
    [internalDirection, internalKey],
  );

  const hasSortableColumns = columns?.some((col) => col.sortable) || false;

  return useMemo(() => {
    if (sorting) {
      const onControlledSort = (key: string) => {
        if (sorting.key !== key) {
          sorting.onSort(key, "asc");
        } else if (sorting.direction === "asc") {
          sorting.onSort(key, "desc");
        } else {
          sorting.onSort(key, null);
        }
      };

      if (sorting.server) {
        return {
          sortedItems: items,
          sortConfig: { key: sorting.key, direction: sorting.direction },
          onSort: onControlledSort,
        };
      }

      const column = columns?.find((c) => c.key === sorting.key);
      const sortFn = column?.sortFn || defaultSort;

      const sorted =
        sorting.direction && sorting.key
          ? [...items].sort((a, b) =>
              sortFn(
                a,
                b,
                sorting.direction as "asc" | "desc",
                sorting.key as string,
              ),
            )
          : items;

      return {
        sortedItems: sorted,
        sortConfig: { key: sorting.key, direction: sorting.direction },
        onSort: onControlledSort,
      };
    }

    if (!hasSortableColumns) {
      return {
        sortedItems: items,
        sortConfig: undefined,
        onSort: () => {},
      };
    }

    if (!internalKey || !internalDirection) {
      return {
        sortedItems: items,
        sortConfig: undefined,
        onSort: onInternalSort,
      };
    }

    const column = columns?.find((c) => c.key === internalKey);
    const sortFn = column?.sortFn || defaultSort;

    const sorted = [...items].sort((a, b) =>
      sortFn(a, b, internalDirection, internalKey),
    );

    return {
      sortedItems: sorted,
      sortConfig: { key: internalKey, direction: internalDirection },
      onSort: onInternalSort,
    };
  }, [
    sorting,
    hasSortableColumns,
    internalKey,
    internalDirection,
    columns,
    items,
    onInternalSort,
  ]);
}
